import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { CartContext } from "components/cart";
import classNames from "lib/classNames";
import { createGlobalStyle } from "styled-components";

const Bundles = ({ line, id, bundles, itemError, soldOut, maxQuantity }) => {
  const { shopify_cart_items, money_format, Utils } = useContext(CartContext);
  const [active_bundle, setActiveBundle] = useState(0);
  const [active_bundle_loading, setActiveBundleLoading] = useState(false);

  const withDefaultBundle =
    bundles.list.length && bundles.list[0].quantity !== 1
      ? [
        {
          id: `default-${id}`,
          quantity: 1,
          reward: 0,
          type: "percentage"
        }
      ].concat(bundles.list)
      : bundles.list;
  const bundlesIndexMap = Utils.BundlesIndexMap(
    Utils.sortBundles(withDefaultBundle)
  );
  const { quantity, price } = shopify_cart_items[line - 1]
  useEffect(() => {
    const selectedBundle = bundlesIndexMap[quantity];
    const maxQuantity = Math.max(...Object.keys(bundlesIndexMap).map(Number));
    if (Number.isInteger(selectedBundle) && selectedBundle !== active_bundle) {
      setActiveBundle(selectedBundle);
    } else if (selectedBundle !== undefined && quantity > maxQuantity) {
      setActiveBundle(withDefaultBundle.length - 1);
    }
  }, [quantity]);

  const { background, color, activeBackground, activeColor } = bundles;

  return (
    <div className={classNames("flex flex-col pt-2")}>
      {withDefaultBundle.map((bundle, i) => {
        const { quantity: nextQuantity } = withDefaultBundle[i + 1] || {
          quantity: Infinity
        };
        const nextStepGTOne = nextQuantity - bundle.quantity > 1;
        const lastBundle = withDefaultBundle.length === i + 1 || nextStepGTOne;
        return (
          <Bundle
            {...{
              bundles,
              bundle,
              id,
              line,
              active_bundle,
              setActiveBundle,
              active_bundle_loading,
              setActiveBundleLoading,
              background,
              color,
              activeBackground,
              activeColor,
              lastBundle,
              itemError,
              soldOut,
              maxQuantity
            }}
            key={bundle.id}
            value={i}
          />
        );
      })}
    </div>
  );
};

const Bundle = ({
  bundles,
  bundle,
  id,
  line,
  value,
  active_bundle,
  active_bundle_loading,
  setActiveBundle,
  setActiveBundleLoading,
  itemError,
  background,
  color,
  activeBackground,
  activeColor,
  lastBundle,
  maxQuantity,
  soldOut
}) => {
  const {
    cart_settings: {
      money_format,
      theme: { font },
      rtl
    },
    update_cart_item,
    shopify_cart_items,
    Utils
  } = useContext(CartContext);
  const { type, reward, quantity } = bundle;
  const cart_item = shopify_cart_items.find((item) => item.id === id);
  const formatted_reward =
    type === "percentage"
      ? `<b>${reward}%</b>`
      : `<b>${Utils.formatMoney(reward * 100)}</b>`;
  const text = bundles.text
    .replace("{{quantity}}", `<b>${quantity}${lastBundle ? "+" : ""}</b>`)
    .replace("{{discount}}", formatted_reward);
  const discountedPrice = Utils.getBundleDiscount(
    bundle,
    quantity,
    cart_item.price,
    money_format
  );
  const individual_price = Utils.formatMoney(
    discountedPrice.plain / quantity,
    money_format
  );
  const prefix = bundles.prefix.replace(
    "{{individual_price}}",
    individual_price
  );

  if (type === "" || reward === "" || quantity === "") return null;
  const checked = active_bundle === value;
  return (
    <button
      className={classNames(
        "flex mb-1 rounded items-center p-1 cursor-pointer border-0",
        {
          "opacity-50 cursor-not-allowed": quantity > maxQuantity.get
        }
      )}
      style={{
        background: Utils.formatColor(checked ? activeBackground : background),
        color: Utils.formatColor(checked ? activeColor : color),
        fontSize: Utils.em(bundles.font_size)
        // padding: bundles.padding,
      }}
      tabIndex="0"
      disabled={quantity > maxQuantity.get}
      onClick={() => {
        if (active_bundle_loading) return;
        const previousActive = active_bundle;
        setActiveBundle(value);
        setActiveBundleLoading(true);
        update_cart_item({ line, variant_id: id, quantity }, (item, items, error) => {
          console.log({ error, item, quantity })
          if (error) {
            console.log(error);
            maxQuantity.set(0);
            soldOut.set(true);
            update_cart_item({ line, variant_id: id, quantity: 0, sold_out: true });
          } else if (item && item.quantity !== quantity) {
            maxQuantity.set(item.quantity);
            setActiveBundle(previousActive);
          }

          setActiveBundleLoading(false);
        });
      }}
    >
      <div
        className={classNames("w-4 h-4 rounded-full p-1 flex-center", {
          "ml-1": rtl,
          "mr-1": !rtl,
          "opacity-0": !checked
        })}
        style={{
          background: Utils.formatColor(checked ? activeColor : color),
          color: Utils.formatColor(checked ? activeBackground : background),
          fontSize: 10
        }}
      >
        {!active_bundle_loading && (
          <svg
            aria-hidden="true"
            focusable="false"
            data-icon="check"
            className={classNames("w-full h-full")}
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
          >
            <path
              fill="currentColor"
              d="M173.898 439.404l-166.4-166.4c-9.997-9.997-9.997-26.206 0-36.204l36.203-36.204c9.997-9.998 26.207-9.998 36.204 0L192 312.69 432.095 72.596c9.997-9.997 26.207-9.997 36.204 0l36.203 36.204c9.997 9.997 9.997 26.206 0 36.204l-294.4 294.401c-9.998 9.997-26.207 9.997-36.204-.001z"
            ></path>
          </svg>
        )}
        {active_bundle_loading && (
          <svg
            aria-hidden="true"
            focusable="false"
            data-icon="spinner-third"
            className={classNames("w-full h-full fa-spin")}
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
          >
            <path
              fill="currentColor"
              d="M456.433 371.72l-27.79-16.045c-7.192-4.152-10.052-13.136-6.487-20.636 25.82-54.328 23.566-118.602-6.768-171.03-30.265-52.529-84.802-86.621-144.76-91.424C262.35 71.922 256 64.953 256 56.649V24.56c0-9.31 7.916-16.609 17.204-15.96 81.795 5.717 156.412 51.902 197.611 123.408 41.301 71.385 43.99 159.096 8.042 232.792-4.082 8.369-14.361 11.575-22.424 6.92z"
            ></path>
          </svg>
        )}
      </div>
      <div
        dangerouslySetInnerHTML={{ __html: text }}
        className={classNames("flex-1 flex-grow font-bold leading-full", {
          "text-right": rtl,
          "text-left": !rtl
        })}
      />
      <span
        className={classNames("leading-full font-bold")}
        dangerouslySetInnerHTML={{
          __html: prefix
        }}
      />
    </button>
  );
};

Bundles.propTypes = {};
export default Bundles;
